import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, ViewEncapsulation, OnInit, ChangeDetectorRef, OnDestroy, SimpleChanges } from '@angular/core';
import { ARStoryOption } from 'src/app/_models/ARStoryOption';
import { FileUploadService } from 'src/app/_services/file-upload.service';
import { MessageService } from 'primeng/api';
import { Storage, ref, listAll, getDownloadURL, deleteObject } from '@angular/fire/storage'; // Updated import + added functions
import { ConfirmationService } from 'primeng/api';
import { UploadStatus } from 'src/app/shared/components/upload-modal/upload-modal.component';
import { fromEvent, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

interface CachedAnimations {
  timestamp: number;
  animations: any[];
}

@Component({
  selector: 'app-scenario-details',
  templateUrl: './scenario-details.component.html',
  styleUrls: ['./scenario-details.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ScenarioDetailsComponent implements OnInit, OnDestroy {

  @Input()
  set element(element: ARStoryOption) {
    this._element = element;
    // Clear any existing text backup when switching elements
    this.textBackup = '';
    
    // Initialize behaviors if they don't exist
    if (element) {
      element.textBehavior = element.textBehavior || 'stop';
      element.animationBehavior = element.animationBehavior || 'loop';
      // Set animationsEnabled to true for existing elements that don't have this property
      if (element.animationsEnabled === undefined) {
        element.animationsEnabled = true;
      }
      // Initialize weatherAnnouncement if it doesn't exist
      if (element.weatherAnnouncement === undefined) {
        element.weatherAnnouncement = false;
      }
    }
    if (element?.animation?.id) {
      this.selectedAnimationUrl = "assets/models/animations/" + element.animation.id + ".glb";
    }
  }
  get element(): ARStoryOption {
    return this._element;
  }
  @Input() animations: any[];
  @Input() clientId: string;
  @Input() projectId: string;

  @Output() elementChange = new EventEmitter<void>();
  @ViewChild('fileInput') fileInput: ElementRef;
  @ViewChild('fileUpload') fileUpload: any;

  private _element: ARStoryOption;
  public selectedModel: any;

  selectedModelUrl: string;

  selectedAnimationUrl: string;

  uploadModalVisible: boolean = false;
  activeStep: number = 0;
  uploadedAnimation: File | null = null;
  uploadedThumbnail: File | null = null;
  animationPreviewUrl: string | null = null;
  thumbnailPreviewUrl: string | null = null;
  animationName: string = '';
  uploadProgress: number = 0;
  isUploading: boolean = false;
  uploadStatus: UploadStatus = { state: 'idle' };
  
  private readonly CACHE_KEY = 'custom-animations-cache';
  private readonly CACHE_DURATION = 1000 * 60 * 60; // 1 hour

  uploadSteps = [
    { number: 1, label: 'Upload Animation' },
    { number: 2, label: 'Name Animation' }
  ];

  defaultAnimations: any[] = [];
  customAnimations: any[] = [];

  showDeleteConfirmation: boolean = false;
  animationToDelete: any = null;
  deleteConfirmationMessage: string = '';

  nameError: string = '';

  private modalBackdropClickSubscription: Subscription;
  closeConfirmationMessage: string = 'You have unsaved changes. Are you sure you want to close?';
  showCloseConfirmation: boolean = false;

  private _feature: any;
  private textBackup: string = '';

  textBehaviorOptions = [
    { label: 'Stop', value: 'stop' },
    { label: 'Loop', value: 'loop' }
  ];

  animationBehaviorOptions = [
    { label: 'Idle', value: 'idle' },
    { label: 'Loop', value: 'loop' }
  ];

  // Animation type selection
  animationTypeOptions = [
    { label: 'Default', value: 'default' },
    { label: 'Custom', value: 'custom' }
  ];

  selectedAnimationType: string = 'default';
  filteredAnimations: any[] = [];

  constructor(
    private fileUploadService: FileUploadService,
    private messageService: MessageService,
    private storage: Storage, // Updated injected type
    private confirmationService: ConfirmationService,
    private cdr: ChangeDetectorRef
  ) {}

  async ngOnInit() {
    this.selectedModel = this.feature.interactiveModel;
    this.selectedModelUrl = this.getModelUrl(this.feature.interactiveModel);
    
    // Initialize default animations
    this.defaultAnimations = this.animations?.filter(anim => !anim.isCustom) || [];
    
    // Load custom animations
    await this.loadCustomAnimations();
  }

  @Input()
  set feature(value: any) {
    this._feature = value;
    if (value?.interactiveModel) {
      this.selectedModel = value.interactiveModel;
      this.selectedModelUrl = this.getModelUrl(value.interactiveModel);
      
      // Reapply current animation if it exists
      if (this._element?.animation) {
        this.selectedAnimationUrl = this.getAnimationUrl(this._element.animation);
      }
      
      // Force change detection
      this.cdr.detectChanges();
    }
  }
  get feature(): any {
    return this._feature;
  }

  // Add ngOnChanges to handle updates
  ngOnChanges(changes: SimpleChanges) {
    if (changes['feature']?.currentValue?.interactiveModel) {
      this.selectedModelUrl = this.getModelUrl(changes['feature'].currentValue.interactiveModel);
      
      // Reapply current animation
      if (this._element?.animation) {
        this.selectedAnimationUrl = this.getAnimationUrl(this._element.animation);
      }
    }
  }

  onAnimationsToggle() {
    // If animations are disabled, clear the animation selection
    if (!this.element.animationsEnabled) {
      this.element.animation = null;
      this.element.animationBehavior = null;
    }
    this.elementChange.emit();
  }

  onWeatherAnnouncementToggle() {
    if (this.element.weatherAnnouncement) {
      // Weather announcement is being enabled - backup current text and clear it
      if (this.element.text && this.element.text.trim()) {
        this.textBackup = this.element.text;
      }
      this.element.text = '';
    } else {
      // Weather announcement is being disabled - restore backed up text if available
      if (this.textBackup && this.textBackup.trim()) {
        this.element.text = this.textBackup;
        this.textBackup = ''; // Clear backup after restoring
      }
    }
    this.elementChange.emit();
  }

  // Update getModelUrl to be more robust
  private getModelUrl(model: any): string {
    if (!model) return "assets/models/14.glb"; // default model
    
    // Handle case where model might be undefined or null
    try {
      return model.isCustom ? model.url : `assets/models/${model.id}.glb`;
    } catch (error) {
      console.warn('Error getting model URL:', error);
      return "assets/models/14.glb";
    }
  }

  // Add helper method to get animation URL
  private getAnimationUrl(animation: any): string {
    if (!animation) return '';
    return animation.isCustom ? 
      animation.url : 
      `assets/models/animations/${animation.id}.glb`;
  }

  private async loadCustomAnimations() {
    // First check cache
    const cachedData = localStorage.getItem(this.CACHE_KEY);
    if (cachedData) {
      const { timestamp, animations } = JSON.parse(cachedData);
      if (Date.now() - timestamp < this.CACHE_DURATION) {
        // Cache is valid, merge with default animations
        this.mergeCustomAnimations(animations);
        return;
      }
    }

    try {

      console.log("Loading animations from " + `clients/${this.clientId}/custom-animations`)

      // List all files in the custom-animations directory
      const storageRef = ref(this.storage, `clients/${this.clientId}/custom-animations`); // Use ref function
      const result = await listAll(storageRef); // Use listAll function (returns Promise)
      
      console.log("Found total of " + result.prefixes.length + " animations");
      const customAnimations = await Promise.all(
        result.prefixes.map(async (prefix) => {
          const animName = prefix.name;
          const animRef = ref(this.storage, `clients/${this.clientId}/custom-animations/${animName}/${animName}.glb`); // Use ref function
          const url = await getDownloadURL(animRef); // Use getDownloadURL function (returns Promise)
          
          return {
            id: animName,
            url: url,
            name: animName,
            label: animName,
            isCustom: true,
            timeCreated: Date.now().toString()
          };
        })
      );

      // Cache the results
      localStorage.setItem(this.CACHE_KEY, JSON.stringify({
        timestamp: Date.now(),
        animations: customAnimations
      }));

      // Merge with default animations
      this.mergeCustomAnimations(customAnimations);

    } catch (error) {
      console.error('Error loading custom animations:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load custom animations'
      });
    }
  }

  private mergeCustomAnimations(customAnimations: any[]) {
    // Store custom animations separately
    this.customAnimations = customAnimations;

    // Store default animations separately
    this.defaultAnimations = this.animations.filter(anim => !anim.isCustom);

    // Keep the combined list for compatibility
    this.animations = [...this.defaultAnimations, ...this.customAnimations];
    
    // Update filtered animations
    this.updateFilteredAnimations();
  }

  addInteractiveOption() {
    let newElement = Object.assign({}, new ARStoryOption());
    this.element.options.push(newElement);
    this.elementChange.emit();
  }

  removeInteractiveOption(option: ARStoryOption) {
    const index = this.element.options.indexOf(option, 0);
    if (index > -1) {
      this.element.options.splice(index, 1);
    }
    this.elementChange.emit();
  }
  triggerFileInput() {
    this.fileInput.nativeElement.click();
  }
  
  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      // Handle file upload logic here
      console.log('File selected:', file.name);
    }
  }

  selectAnimation(animation: any) {
    this.element.animation = animation;
    this.elementChange.emit();
    this.selectedAnimationUrl = this.getAnimationUrl(animation);
    console.log("selected animation: " + this.selectedAnimationUrl);
  }

  onAnimationTypeChange() {
    this.updateFilteredAnimations();
  }

  updateFilteredAnimations() {
    if (this.selectedAnimationType === 'default') {
      this.filteredAnimations = this.defaultAnimations || [];
    } else {
      this.filteredAnimations = this.customAnimations || [];
    }
  }

  onAnimationDropdownChange(selectedAnimation: any) {
    if (selectedAnimation) {
      this.selectAnimation(selectedAnimation);
    }
  }

  deleteAnimationFromDropdown(animation: any, event: Event) {
    event.stopPropagation();
    this.confirmDeleteAnimation(animation);
  }
  onAnimFileDropped(files: any) {
    if (files && files.length > 0) {
      const file = files[0];
      this.element.animFile = {
        name: file.name,
        url: URL.createObjectURL(file),
        size: file.size,
        isUploaded: false,
        type: "Anim",
        file: file
      };
      this.elementChange.emit();
    }
  }

  showUploadModal() {
    this.uploadModalVisible = true;
    this.activeStep = 0;
    this.resetUploadStates();
    this.subscribeToBackdropClicks();
  }

  private subscribeToBackdropClicks() {
    // First unsubscribe if there's an existing subscription
    if (this.modalBackdropClickSubscription) {
      this.modalBackdropClickSubscription.unsubscribe();
    }

    // Subscribe to clicks on modal backdrop (p-dialog-mask)
    this.modalBackdropClickSubscription = fromEvent(document, 'click')
      .pipe(
        filter((event: Event) => {
          const target = event.target as HTMLElement;
          return target.classList.contains('p-dialog-mask');
        })
      )
      .subscribe(() => {
        this.handleModalClose();
      });
  }

  private handleModalClose() {
    // Check if there are unsaved changes
    if (this.hasUnsavedChanges()) {
      this.showCloseConfirmation = true;
    } else {
      this.closeUploadModal();
    }
  }

  private hasUnsavedChanges(): boolean {
    // Return true if there are unsaved changes
    return !!(this.uploadedAnimation || 
             (this.animationName && this.animationName.trim()) ||
             this.uploadStatus.state === 'uploading');
  }

  onCloseConfirm() {
    this.showCloseConfirmation = false;
    this.closeUploadModal();
  }

  onCloseCancel() {
    this.showCloseConfirmation = false;
  }

  resetUploadStates() {
    this.animationPreviewUrl = null;
    this.uploadedAnimation = null;
    this.animationName = '';
  }

  onAnimationUpload(event: any) {
    const file = event.files[0];
    if (this.isValidAnimationFile(file)) {
      this.uploadedAnimation = file;
      this.animationPreviewUrl = URL.createObjectURL(file);
    }
  }

  isValidAnimationFile(file: File): boolean {
    const validTypes = ['.glb', '.gltf'];
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    return validTypes.includes(fileExtension);
  }

  validateAnimationNameInput(name: string): void {
    if (!name.trim()) {
      this.nameError = 'Please enter an animation name';
      return;
    }

    if (!/^[a-zA-Z0-9-_]+$/.test(name)) {
      this.nameError = 'Animation name can only contain letters, numbers, hyphens and underscores';
      return;
    }

    const isDuplicate = this.customAnimations.some(
      anim => anim.name.toLowerCase() === name.toLowerCase()
    );

    if (isDuplicate) {
      this.nameError = `An animation with this name already exists`;
      return;
    }

    this.nameError = '';
  }

  validateAnimationName(): boolean {
    this.validateAnimationNameInput(this.animationName);
    return !this.nameError;
  }

  async finishUpload() {
    if (!this.uploadedAnimation || !this.validateAnimationName()) {
      return;
    }

    this.uploadStatus = { state: 'uploading' };
    this.isUploading = true;
    this.uploadProgress = 0;

    try {
      // Upload animation file
      const animPath = `clients/${this.clientId}/custom-animations/${this.animationName}/${this.animationName}.glb`;
      const animUrl = await this.fileUploadService.uploadFile(this.uploadedAnimation, animPath);
      this.uploadProgress = 100;

      const newAnimation = {
        id: this.animationName,
        url: animUrl,
        isCustom: true,
        name: this.animationName,
        label: this.animationName,
        timeCreated: Date.now().toString()
      };

      // Add to animations arrays
      this.customAnimations.push(newAnimation);
      this.animations.push(newAnimation);

      // Select the new animation
      this.selectAnimation(newAnimation);

      // Update status to success
      this.uploadStatus = { 
        state: 'success',
        message: 'Animation uploaded successfully!' 
      };
      this.isUploading = false;

      // Invalidate cache
      localStorage.removeItem(this.CACHE_KEY);

      // Reload custom animations to ensure everything is in sync
      await this.loadCustomAnimations();

      // Force change detection
      this.cdr.detectChanges();

    } catch (error) {
      console.error('Upload error:', error);
      this.uploadStatus = { 
        state: 'error',
        message: 'Failed to upload animation',
        error: error 
      };
      this.isUploading = false;
    }
  }

  closeUploadModal() {
    this.uploadModalVisible = false;
    this.activeStep = 0;
    this.resetUploadStates();
    this.uploadStatus = { state: 'idle' };
    
    // Unsubscribe from backdrop clicks
    if (this.modalBackdropClickSubscription) {
      this.modalBackdropClickSubscription.unsubscribe();
    }
  }

  onBack() {
    // Only allow going back if not on first step
    if (this.activeStep > 0) {
      this.activeStep--;
    }
  }

  canProceed(): boolean {
    switch (this.activeStep) {
      case 0:
        return !!this.uploadedAnimation; // Only proceed if animation is selected
      case 1:
        return !!this.animationName && /^[a-zA-Z0-9-_]+$/.test(this.animationName);
      default:
        return false;
    }
  }

  showBackButton(): boolean {
    return this.activeStep > 0 || this.uploadStatus.state !== 'idle';
  }

  getPrimaryButtonLabel(): string {
    if (this.uploadStatus.state === 'success') {
      return 'Finish';
    }
    return this.activeStep === 1 ? 'Upload' : 'Next';
  }

  onFinish() {
    if (this.uploadStatus.state === 'success') {
      this.closeUploadModal();
    } else if (this.activeStep === 1) {
      this.finishUpload();
    } else {
      this.nextStep();
    }
  }

  nextStep() {
    if (this.activeStep < 1 && this.canProceed()) {
      this.activeStep++;
    }
  }

  confirmDeleteAnimation(animation: any) {
    this.animationToDelete = animation;
    this.deleteConfirmationMessage = `Are you sure you want to delete "${animation.name}"? This action cannot be undone.`;
    this.showDeleteConfirmation = true;
  }

  async onDeleteConfirm() {
    if (this.animationToDelete) {
      await this.deleteAnimation(this.animationToDelete);
      this.animationToDelete = null;
    }
  }

  onDeleteCancel() {
    this.animationToDelete = null;
  }

  async deleteAnimation(animation: any) {
    try {
      // Delete from storage
      const animRef = ref(this.storage, `clients/${this.clientId}/custom-animations/${animation.name}/${animation.name}.glb`); // Use ref function
      await deleteObject(animRef); // Use deleteObject function (returns Promise)

      // Remove from lists
      this.customAnimations = this.customAnimations.filter(a => a.id !== animation.id);
      this.animations = this.animations.filter(a => a.id !== animation.id);

      // Clear selection if this animation was selected
      if (this.element.animation?.id === animation.id) {
        this.element.animation = this.defaultAnimations[0];
        this.selectAnimation(this.defaultAnimations[0]);
      }

      // Invalidate cache
      localStorage.removeItem(this.CACHE_KEY);

      // Force change detection
      this.cdr.detectChanges();

      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Animation deleted successfully'
      });

    } catch (error) {
      console.error('Error deleting animation:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to delete animation'
      });
    }
  }

  ngOnDestroy() {
    // Clean up subscription
    if (this.modalBackdropClickSubscription) {
      this.modalBackdropClickSubscription.unsubscribe();
    }
  }
}